import React, { useRef, useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  TextField,
  IconButton,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Check,
  Clear
} from '@mui/icons-material';
import { COLORS, isValidHex } from '../../shared/colors';
import { CHARACTER_LIMITS, TOAST_MESSAGES, USER_ROLES } from '../../shared/appConstants';
import { labelSelectorStyles } from '../../shared/globalSX';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { createTicketLabel, createTicketSprint, updateTicketDropdown, deleteTicketDropdown } from '../../store/ticketSlice';
import OverflowTooltip from '../../shared/OverflowTooltip';
import CustomColorPopover from '../shared/CustomColorPopover';
import CommonDeleteConfirmDialog from '../shared/CommonDeleteConfirmDialog';
import './DropdownSelector.scss';

const DropdownSelector = ({ 
  value, 
  onChange, 
  items = [], 
  projectId, 
  userRole,
  category, // 'labels' or 'sprint'
  label, // Translation key for label
  placeholder, // Translation key for placeholder
  createText, // Translation key for create text
  multiple = false, // Multi-select or single-select
  withColor = false, // Whether to show color picker
  createOptionPosition = 'bottom' // 'top' or 'bottom'
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  
  // Check if user can manage items (edit/delete - only admin and pm)
  const canManage = userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.PM;
  const [editingItem, setEditingItem] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemColor, setNewItemColor] = useState(COLORS.primary);
  const createLockRef = useRef(false);
  const [isCreatingSubmitting, setIsCreatingSubmitting] = useState(false);
  const deleteLockRef = useRef(false);
  const [isDeletingSubmitting, setIsDeletingSubmitting] = useState(false);
  const [createPickerAnchor, setCreatePickerAnchor] = useState(null);
  const [editPickerAnchor, setEditPickerAnchor] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const handleEdit = (item) => {
    setIsCreating(false);
    setNewItemName('');
    setNewItemColor(COLORS.primary);
    setEditingItem({ ...item, newName: item.name, newColor: item.colour || COLORS.primary });
  };

  const handleDelete = async (itemId) => {
    if (deleteLockRef.current) return;
    deleteLockRef.current = true;
    setIsDeletingSubmitting(true);
    try {
      await dispatch(deleteTicketDropdown({ id: itemId, category })).unwrap();
      // Clear selection if deleted item was selected
      if (multiple) {
        if (Array.isArray(value) && value.includes(itemId)) {
          onChange(value.filter(id => id !== itemId));
        }
      } else {
        if (value === itemId) {
          onChange('');
        }
      }
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.TICKET_ATTRIBUTE_DELETE_FAILED;
      toast.error(errorMessage);
      throw error;
    } finally {
      deleteLockRef.current = false;
      setIsDeletingSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      await handleDelete(itemToDelete.id);
    } catch (err) {
      // Handle error if needed
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const getDeleteTitle = () => {
    if (category === 'labels') return t('taskModal.deleteLabel');
    if (category === 'sprint') return t('taskModal.deleteSprint');
    return t('common.delete');
  };

  const getDeleteQuestion = () => {
    if (category === 'labels') return t('taskModal.confirmDeleteLabel');
    if (category === 'sprint') return t('taskModal.confirmDeleteSprint');
    return t('common.deleteConfirmation');
  };

  const handleUpdate = async () => {
    if (editingItem?.newName?.trim() && (!withColor || isValidHex(editingItem.newColor))) {
      try {
        await dispatch(updateTicketDropdown({ 
          id: editingItem.id, 
          name: editingItem.newName, 
          colour: withColor ? editingItem.newColor : undefined,
          category 
        })).unwrap();
      } catch (error) {
        const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.TICKET_ATTRIBUTE_UPDATE_FAILED;
        toast.error(errorMessage);
      } finally {
        setEditingItem(null);
      }
    }
  };

  const handleCreate = async () => {
    if (!newItemName.trim() || createLockRef.current || (withColor && !isValidHex(newItemColor))) return;

    createLockRef.current = true;
    setIsCreatingSubmitting(true);

    try {
      let result;
      if (category === 'labels') {
        result = await dispatch(createTicketLabel({
          name: newItemName,
          colour: newItemColor,
          projectId
        })).unwrap();
      } else if (category === 'sprint') {
        result = await dispatch(createTicketSprint({
          name: newItemName,
          colour: withColor ? newItemColor : undefined,
          projectId
        })).unwrap();
      }

      if (result?.id) {
        if (multiple) {
          const currentValue = Array.isArray(value) ? value : [];
          if (!currentValue.includes(result.id)) {
            onChange([...currentValue, result.id]);
          }
        } else {
          onChange(result.id);
        }
      }
      setNewItemName('');
      setNewItemColor(COLORS.primary);
      setIsCreating(false);
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.TICKET_ATTRIBUTE_CREATE_FAILED;
      toast.error(errorMessage);
      createLockRef.current = false;
      setIsCreatingSubmitting(false);
    }
  };

  const handleCancelCreate = () => {
    setNewItemName('');
    setNewItemColor(COLORS.primary);
    setIsCreating(false);
  };

  const isSelected = (itemId) => {
    if (multiple) {
      return Array.isArray(value) && value.includes(itemId);
    }
    return value === itemId;
  };


  const renderValue = (selected) => {
    if (multiple) {
      const selectedArray = Array.isArray(selected) ? selected : [];
      if (selectedArray.length === 0) {
        return <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>{t(placeholder)}</span>;
      }
      const selectedItems = selectedArray.map(val => items.find(i => i.id === val)).filter(Boolean);
      
      // Show first 2 chips, then "+n" for the rest
      const displayItems = selectedItems.slice(0, 2);
      const remainingCount = selectedItems.length - 2;
      
      return (
        <Box className="dropdown-selector__render-value-container">
          {displayItems.map((item) => {
            const color = item.colour || COLORS.gray[300];
            return (
              <OverflowTooltip
                key={item.id}
                title={item.name}
                tooltipWidth={300}
                placement="top-start"
                component={Chip}
                label={item.name}
                size="small"
                sx={{
                  ...labelSelectorStyles.chip(color),
                  maxWidth: '120px',
                  flexShrink: 0,
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                    maxWidth: '100%'
                  }
                }}
              />
            );
          })}
          {remainingCount > 0 && (
            <OverflowTooltip
              title={selectedItems.slice(2).map(i => i.name).join(', ')}
              tooltipWidth={300}
              placement="top-start"
              component={Chip}
              label={`+${remainingCount}`}
              size="small"
              sx={{
                ...labelSelectorStyles.chip(COLORS.gray[300]),
                flexShrink: 0,
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block'
                }
              }}
            />
          )}
        </Box>
      );
    } else {
      if (!selected) return '';
      const item = items.find(i => i.id === selected);
      if (!item) return '';
      const color = item.colour || COLORS.gray[300];
      return (
        <Box className="dropdown-selector__render-value-container">
          <Box className="dropdown-selector__single-select-inner">
            <OverflowTooltip
              title={item.name}
              tooltipWidth={300}
              placement="top-start"
              component={Chip}
              label={item.name}
              size="small"
              sx={{ ...labelSelectorStyles.chip(color) }}
              className="dropdown-selector__chip-single"
            />
          </Box>
        </Box>
      );
    }
  };

  const createOptionBorderStyle = createOptionPosition === 'top'
    ? { borderBottom: `1px solid ${COLORS.gray[200]}` }
    : labelSelectorStyles.createOption;

  const createFormBorderStyle = createOptionPosition === 'top'
    ? { borderBottom: `1px solid ${COLORS.gray[200]}` }
    : { borderTop: `1px solid ${COLORS.gray[200]}` };

  const createOptionMenuItem = !isCreating && (
    <MenuItem
      className="create-option"
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setEditingItem(null);
        setIsCreating(true);
      }}
      sx={createOptionBorderStyle}
    >
      <Box sx={labelSelectorStyles.createContainer}>
        <Add sx={{ fontSize: 16 }} />
        <span>{t(createText)}</span>
      </Box>
    </MenuItem>
  );

  const createFormMenuItem = isCreating && (
    <MenuItem
      sx={{
        ...labelSelectorStyles.createFormContainer,
        ...createFormBorderStyle,
        borderTop: createOptionPosition === 'top' ? 'none' : `1px solid ${COLORS.gray[200]}`,
        borderBottom: createOptionPosition === 'top' ? `1px solid ${COLORS.gray[200]}` : 'none'
      }}
      disableRipple
      onMouseDown={(e) => e.preventDefault()}
    >
      <Box sx={labelSelectorStyles.editingContainer}>
        <Box sx={labelSelectorStyles.editingTextField}>
          <TextField
            size="small"
            placeholder={t(placeholder)}
            value={newItemName}
            onChange={(e) => {
              e.stopPropagation();
              setNewItemName(e.target.value);
            }}
            inputProps={{ maxLength: CHARACTER_LIMITS.LABEL_NAME }}
            helperText={`${newItemName.length}/${CHARACTER_LIMITS.LABEL_NAME} characters`}
            fullWidth
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCreate();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                handleCancelCreate();
              }
            }}
            onMouseDown={(e) => e.stopPropagation()}
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
        </Box>
        <Box sx={labelSelectorStyles.inputControlsWrapper}>
          {withColor && (
            <>
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  setCreatePickerAnchor(e.currentTarget);
                }}
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '4px',
                  cursor: 'pointer',
                  border: `1px solid ${COLORS.gray[400]}`,
                  bgcolor: isValidHex(newItemColor) ? newItemColor : '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 'inset 0 0 4px rgba(0,0,0,0.15)',
                  '&:hover': { transform: 'scale(1.1)' }
                }}
              />
              <CustomColorPopover
                open={Boolean(createPickerAnchor)}
                anchorEl={createPickerAnchor}
                onClose={() => setCreatePickerAnchor(null)}
                colour={newItemColor}
                onChange={setNewItemColor}
              />
            </>
          )}
          <IconButton
            size="small"
            aria-label="confirm-create"
            onClick={(e) => {
              e.stopPropagation();
              const btn = e.currentTarget;
              btn.disabled = true;
              handleCreate().finally(() => {
                btn.disabled = false;
              });
            }}
            sx={labelSelectorStyles.successButton}
            disabled={isCreatingSubmitting || !newItemName.trim() || newItemName.length > CHARACTER_LIMITS.LABEL_NAME || (withColor && !isValidHex(newItemColor))}
          >
            <Check sx={labelSelectorStyles.iconButton} />
          </IconButton>
          <IconButton size="small" aria-label="cancel-create" onClick={(e) => { e.stopPropagation(); handleCancelCreate(); }} sx={labelSelectorStyles.errorButton}>
            <Clear sx={labelSelectorStyles.iconButton} />
          </IconButton>
        </Box>
      </Box>
    </MenuItem>
  );

  return (
    <>
      <FormControl fullWidth variant="outlined" sx={labelSelectorStyles.formControl}>
        <InputLabel>{t(label)}</InputLabel>
        <Select
          multiple={multiple}
          value={value || (multiple ? [] : '')}
          onChange={(e) => {
            if (multiple) {
              const filteredValue = e.target.value.filter(val => val !== '' && val != null);
              onChange(filteredValue);
            } else {
              onChange(e.target.value || '');
            }
          }}
          onClose={(e) => {
            setEditingItem(null);
            setIsCreating(false);
            setNewItemName('');
            setNewItemColor(COLORS.primary);
          }}
          label={t(label)}
          renderValue={renderValue}
          MenuProps={labelSelectorStyles.menuProps}
          className="dropdown-selector__select"
        >
          {createOptionPosition === 'top' && createOptionMenuItem}
          {createOptionPosition === 'top' && createFormMenuItem}
          {items.map((item) => (
            <MenuItem 
              key={item.id} 
              value={editingItem?.id === item.id ? '' : item.id}
              onClick={(e) => {
                // Allow unselecting for single select by clicking the same item
                if (!multiple && value === item.id && !editingItem) {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange('');
                  // Close the select menu
                  setTimeout(() => {
                    const selectElement = document.activeElement;
                    if (selectElement && selectElement.blur) {
                      selectElement.blur();
                    }
                  }, 0);
                }
              }}
            >
              {editingItem?.id === item.id ? (
                <Box sx={labelSelectorStyles.editingContainer}>
                  <Box sx={labelSelectorStyles.editingTextField}>
                    <TextField
                      size="small"
                      value={editingItem.newName}
                      onChange={(e) => {
                        e.stopPropagation();
                        setEditingItem(prev => ({ ...prev, newName: e.target.value }));
                      }}
                      inputProps={{ maxLength: CHARACTER_LIMITS.LABEL_NAME }}
                      helperText={`${editingItem.newName.length}/${CHARACTER_LIMITS.LABEL_NAME} characters`}
                      fullWidth
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleUpdate();
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          setEditingItem(null);
                        }
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Box>
                   <Box sx={labelSelectorStyles.inputControlsWrapper}>
                    {withColor && (
                      <>
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditPickerAnchor(e.currentTarget);
                          }}
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '4px',
                            cursor: 'pointer',
                            border: `1px solid ${COLORS.gray[400]}`,
                            bgcolor: isValidHex(editingItem.newColor) ? editingItem.newColor : '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: 'inset 0 0 4px rgba(0,0,0,0.15)',
                            '&:hover': { transform: 'scale(1.1)' }
                          }}
                        />
                        <CustomColorPopover
                          open={Boolean(editPickerAnchor)}
                          anchorEl={editPickerAnchor}
                          onClose={() => setEditPickerAnchor(null)}
                          colour={editingItem.newColor}
                          onChange={(val) => setEditingItem(prev => ({ ...prev, newColor: val }))}
                        />
                      </>
                    )}
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleUpdate(); }} disabled={editingItem.newName.length > CHARACTER_LIMITS.LABEL_NAME || !editingItem.newName.trim() || (withColor && !isValidHex(editingItem.newColor))} sx={labelSelectorStyles.successButton}>
                      <Check sx={labelSelectorStyles.iconButton} />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingItem(null); }} sx={labelSelectorStyles.errorButton}>
                      <Clear sx={labelSelectorStyles.iconButton} />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <Box sx={labelSelectorStyles.labelContainer}>
                  <Box sx={labelSelectorStyles.labelInfo}>
                    {withColor && item.colour && (
                      <Box sx={{ ...labelSelectorStyles.colorDot, bgcolor: item.colour, flexShrink: 0 }} />
                    )}
                    <OverflowTooltip title={item.name} tooltipWidth={550} className="overflow-tooltip-flex" placement="top-start" />
                  </Box>
                  {!isSelected(item.id) && !item.status && canManage && (
                    <Box sx={labelSelectorStyles.actionButtons}>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(item); }} sx={labelSelectorStyles.actionIconButton}>
                        <Edit sx={{ fontSize: 14 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="delete-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setItemToDelete(item);
                          setDeleteDialogOpen(true);
                        }}
                        sx={labelSelectorStyles.actionIconButton}
                        disabled={isDeletingSubmitting}
                      >
                        <Delete sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              )}
            </MenuItem>
          ))}
          {createOptionPosition !== 'top' && createOptionMenuItem}
          {createOptionPosition !== 'top' && createFormMenuItem}
        </Select>
      </FormControl>
      <CommonDeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title={getDeleteTitle()}
        question={getDeleteQuestion()}
        itemName={itemToDelete?.name || ''}
        warning={t('taskModal.actionCannotBeUndone')}
        confirmText={getDeleteTitle()}
        cancelText={t('common.cancel')}
      />
    </>
  );
};

export default DropdownSelector;
